import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { getV1CompaniesTreadIdTreadId } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import TextInput from '~components/FormFields/v2/TextInput';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { CompanyTypeahead } from '~hooks/useCompany/models';
import connection from '~services/connectionModule';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';

import CustomAlert from './CustomAlert';

const searchCompanyByTreadIdSchema = yup.object().shape({
  treadId: yup.string().required(t('form_validation_errors.required_simple')),
});
type SearchCompanyByTreadIdDto = yup.InferType<typeof searchCompanyByTreadIdSchema>;

interface SearchCompanyByTreadIdFormProps {
  onSelectedCompanyChange: (company: CompanyTypeahead | null, treadId: string) => void;
}

export default function SearchCompanyByTreadIdForm({
  onSelectedCompanyChange,
}: SearchCompanyByTreadIdFormProps) {
  const { control, handleSubmit, watch } = useForm<SearchCompanyByTreadIdDto>({
    resolver: yupResolver(searchCompanyByTreadIdSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { treadId: '' },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showNotFoundError, setShowNotFoundError] = useState(false);

  const watchedTreadId = watch('treadId');
  const shouldDisableSubmitButton = watchedTreadId.trim().length === 0;

  const onSubmit = useCallback(
    async (data: SearchCompanyByTreadIdDto) => {
      const { treadId } = data;

      try {
        setIsLoading(true);
        setShowNotFoundError(false);
        onSelectedCompanyChange(null, treadId);

        const response = await withSentryIgnoreRules(
          SentryIgnoreRule.IGNORE_COMPANY_BY_TREAD_ID_NOT_FOUND_WHEN_CONNECTING_ACCOUNT,
          () => getV1CompaniesTreadIdTreadId({ path: { 'tread-id': treadId } }),
        );
        const company = CompanyTypeahead.parse(response.data.data);

        onSelectedCompanyChange(company, treadId);
      } catch (error) {
        if (
          isAxiosError(error) &&
          (error.response?.status === 400 || error.response?.status === 404)
        ) {
          setShowNotFoundError(true);
        } else {
          connection.handleRequestError(
            error,
            t('error_messages.company.failed_to_fetch_company_by_tread_id'),
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onSelectedCompanyChange],
  );

  useEffect(() => {
    return () => {
      setIsLoading(false);
      setShowNotFoundError(false);
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        component="form"
        data-test-id="search-company-by-tread-id-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={(theme) => ({
          alignItems: 'center',
          display: 'flex',
          gap: theme.spacing(2),
          mt: 3,
        })}
      >
        <FormFieldLabel
          label={`${t('form_fields.search_by_tread_id')}`}
          variant="body1"
          sx={{ flexShrink: 0 }}
        />

        <TextInput control={control} isRequired name="treadId" />

        <SmallLoadingButton
          color="primary"
          data-test-id="connect-account-btn"
          disabled={shouldDisableSubmitButton}
          loading={isLoading}
          type="submit"
          variant="contained"
          sx={{ height: '36px !important' }}
        >
          {t('form_fields.search')}
        </SmallLoadingButton>
      </Box>

      {showNotFoundError && (
        <CustomAlert
          message={t('errors.no_company_found_with_tread_id')}
          variant="error"
        />
      )}
    </Box>
  );
}
